/**
 * Game Context – current sport/game (Table Tennis, Pickleball, Badminton, Football).
 * Persisted in localStorage; header dropdown switches game and filters all data by gameId.
 * Super admin can disable sports via config/enabledGames; only enabled games are shown.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as readApi from '../api/readApi';
import { GAMES, DEFAULT_GAME_ID, getGameById } from '../constants/games';

const STORAGE_KEY = 'gradedplay_currentGameId';

const GameContext = createContext();

export function useGame() {
  return useContext(GameContext);
}

/** Resolve list of games to show: null = still loading, [] = all, [...ids] = only these. */
function resolveEnabledGames(enabledGameIds) {
  if (enabledGameIds === null) return GAMES;
  if (!Array.isArray(enabledGameIds) || enabledGameIds.length === 0) return GAMES;
  return GAMES.filter((g) => enabledGameIds.includes(g.id));
}

export function GameProvider({ children }) {
  const { currentUser } = useAuth();
  const [enabledGameIds, setEnabledGameIds] = useState(null);
  const [gameId, setGameIdState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && GAMES.some((g) => g.id === saved)) return saved;
    } catch (_) {}
    return DEFAULT_GAME_ID;
  });

  useEffect(() => {
    if (!currentUser) {
      setEnabledGameIds([]);
      return;
    }
    let cancelled = false;
    readApi.getConfig()
      .then((data) => {
        if (cancelled) return;
        setEnabledGameIds(Array.isArray(data?.gameIds) ? data.gameIds : []);
      })
      .catch(() => {
        if (!cancelled) setEnabledGameIds([]);
      });
    return () => { cancelled = true; };
  }, [currentUser]);

  const games = resolveEnabledGames(enabledGameIds);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, gameId);
    } catch (_) {}
  }, [gameId]);

  useEffect(() => {
    if (games.length === 0) return;
    const allowed = games.some((g) => g.id === gameId);
    if (!allowed) {
      const first = games[0]?.id || DEFAULT_GAME_ID;
      if (GAMES.some((g) => g.id === first)) setGameIdState(first);
    }
  }, [games, gameId]);

  const currentGame = getGameById(gameId);

  function setGameId(id) {
    if (games.some((g) => g.id === id)) {
      setGameIdState(id);
    }
  }

  const value = {
    gameId,
    currentGame,
    games,
    setGameId,
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}
